import { Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { Confounder, ImpactMetric, MetricPoint } from '@/services/changelog';
import { ACCOUNTS, countLabel, formatMetricValue } from './changelog-labels';
import { SOURCE_LABELS } from '../onboarding/onboarding-labels';
import CohortBreakdown from './CohortBreakdown';

interface Props {
  metric: ImpactMetric;
  /** Entry slug, passed down so a cohort bar can fetch the accounts behind it. */
  slug: string;
  /** Date of the change this page is about. Rendered as the emphasised marker. */
  changeDate: string;
  /** Other changes overlapping the window, drawn recessive so they read as context. */
  confounders: Confounder[];
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/**
 * The trailing window a point covers, "5 to 11 Aug". Without it "20 signups"
 * under a single date reads as a data error rather than a week's worth.
 */
const windowLabel = (endIso: string, trailingDays: number) => {
  // One-day points are plain dates, not windows.
  if (trailingDays <= 1) return shortDate(endIso);
  const end = Date.parse(`${endIso}T00:00:00Z`);
  const start = new Date(end - (trailingDays - 1) * 86_400_000);
  const sameMonth = start.getUTCMonth() === new Date(end).getUTCMonth();
  const from = sameMonth ? String(start.getUTCDate()) : shortDate(start.toISOString().slice(0, 10));
  return `${from} to ${shortDate(endIso)}`;
};

/** Tooltip heading, "Week 2 to 8 Jul": the window is the unit, so it is the title. */
const weekLabel = (endIso: string, trailingDays: number) =>
  `${trailingDays === 7 ? 'Week ' : ''}${windowLabel(endIso, trailingDays)}`;

/**
 * Each overlapping-change marker gets its own colour AND dash pattern, so no
 * two markers are ever identical (and identity never rides on colour alone);
 * the legend keys on the colour with a solid dot. The page's own change keeps
 * the amber marker. The colour values live as CSS vars on the CardContent so
 * both the chart and the legend can read them and dark mode can flip them,
 * same as --marker.
 */
const CONFOUNDER_STYLES = [
  { varName: '--confounder-1', dash: '3 3' },
  { varName: '--confounder-2', dash: '1 3' },
  { varName: '--confounder-3', dash: '8 3' },
];
const confounderStyle = (i: number) => CONFOUNDER_STYLES[i % CONFOUNDER_STYLES.length];

/** Axis tick: the plain date for one-day points, "3-9 Aug" for windows. */
const axisWindow = (endIso: string, trailingDays: number) => {
  if (trailingDays <= 1) return shortDate(endIso);
  const end = Date.parse(`${endIso}T00:00:00Z`);
  const start = new Date(end - (trailingDays - 1) * 86_400_000);
  const sameMonth = start.getUTCMonth() === new Date(end).getUTCMonth();
  const from = sameMonth ? String(start.getUTCDate()) : shortDate(start.toISOString().slice(0, 10));
  return `${from}-${shortDate(endIso)}`;
};

/**
 * Every key any metric's breakdown can carry, in display order. Signups split
 * by method; the booking rate by where the booking happened; the trial rate by
 * what the first paid plan was; free-at-signup by what the non-free rest did.
 */
const BREAKDOWN_ORDER = [
  'email', 'google', 'slack', 'other',
  'onboarding', 'notification-bar', 'notification-bar-close-modal', 'direct', 'ghl',
  'purchase',
  'paid', 'unfinished',
  'estimated',
];
const BREAKDOWN_LABELS: Record<string, string> = {
  email: 'Email',
  google: 'Google',
  slack: 'Slack',
  other: 'Other',
  // Booking sources wear the exact names the Appointments page uses
  // (Onboarding, Notification bar, ...), so the two screens read as one.
  ...SOURCE_LABELS,
  purchase: 'Direct purchase',
  paid: 'Paid right away',
  unfinished: 'Never finished',
  // Accounts with no stripe_price_id yet, valued at their plan's CURRENT list
  // price. Reads high for anyone grandfathered, so it is named, not hidden.
  // Disappears once backfill-subscription-prices has run.
  estimated: 'Priced at list, not yet backfilled',
};

/**
 * Keys that merely restate the numerator say nothing the bubble has not already
 * said, so they are dropped. 'trial' used to be one: while the numerator was
 * trials only, "Trial 9" under "9 of 28" was noise. Now that direct purchases
 * count too, the split is the whole point, so nothing is redundant.
 */
const REDUNDANT_BREAKDOWN_KEYS: string[] = [];

/** "Email 22 · Google 6 · Slack 2": what a point's numerator (or remainder) is made of. */
const breakdownLabel = (breakdown: Record<string, number>) =>
  Object.entries(breakdown)
    .filter(([key, n]) => n > 0 && !REDUNDANT_BREAKDOWN_KEYS.includes(key))
    .sort(([a], [b]) => {
      const ia = BREAKDOWN_ORDER.indexOf(a);
      const ib = BREAKDOWN_ORDER.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    })
    .map(([key, n]) => `${BREAKDOWN_LABELS[key] ?? key} ${n}`)
    .join(' · ');

/**
 * The X axis is categorical, so a ReferenceLine only renders when its date is
 * exactly one of the plotted points. Returns the nearest plotted date, or null
 * when the target falls outside the series entirely (in which case a marker
 * would be meaningless rather than merely misplaced).
 */
const snapToSeries = (target: string, series: MetricPoint[]): string | null => {
  if (series.length === 0) return null;

  const t = Date.parse(`${target}T00:00:00Z`);
  const first = Date.parse(`${series[0].date}T00:00:00Z`);
  const last = Date.parse(`${series[series.length - 1].date}T00:00:00Z`);
  if (t < first || t > last) return null;

  let best = series[0].date;
  let bestDiff = Math.abs(t - first);
  for (const point of series) {
    const diff = Math.abs(Date.parse(`${point.date}T00:00:00Z`) - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = point.date;
    }
  }
  return best;
};

/**
 * One series per chart. That removes the need for a legend and for a
 * categorical palette entirely, so no two lines can end up indistinguishable.
 */
/**
 * Stacked-bar series colours, one CSS var per sign-in method (light and dark
 * values live on the CardContent). Both palettes pass the colour-vision and
 * contrast checks and stay clear of the amber/violet/teal marker lines.
 */
const METHOD_VARS: Record<string, string> = {
  email: 'var(--m-email)',
  google: 'var(--m-google)',
  slack: 'var(--m-slack)',
  other: 'var(--m-other)',
};

const ImpactChart = ({ metric, slug, changeDate, confounders }: Props) => {
  const population = metric.population ?? ACCOUNTS;
  // Count metrics render as one stacked column per point, split by sign-in
  // method (picked from four drawn options); rate metrics stay lines.
  const stackKeys =
    metric.unit === 'count'
      ? BREAKDOWN_ORDER.filter(
          (k) => k in METHOD_VARS && metric.series.some((p) => (p.breakdown?.[k] ?? 0) > 0),
        )
      : [];
  const isStacked = stackKeys.length > 0;
  // A count is a volume, so it gets columns whether or not its breakdown has
  // colour-mapped keys. Badge signups split by SOURCE FORM, an open set with no
  // fixed colours, and a line through a handful of whole signups reads as a
  // trend the data cannot support. Those fall back to one plain column series,
  // with the forms still named in the tooltip.
  const isBars = metric.unit === 'count';
  const barKeys = isStacked ? stackKeys : ['value'];
  const barData = isBars ? metric.series.map((p) => ({ ...p, ...(p.breakdown ?? {}) })) : [];

  const config: ChartConfig = isStacked
    ? Object.fromEntries(
        stackKeys.map((k) => [k, { label: BREAKDOWN_LABELS[k] ?? k, color: METHOD_VARS[k] }]),
      )
    : isBars
      ? { value: { label: metric.label, color: 'var(--chart-3)' } }
      : { [metric.key]: { label: metric.label, color: 'var(--chart-3)' } };

  const changeAt = snapToSeries(changeDate, metric.series);

  // Only confounders whose marker actually rendered. A legend entry for a line
  // that snapToSeries rejected would claim something the chart does not show.
  const shownConfounders = confounders
    .map((c) => ({ ...c, at: snapToSeries(c.date, metric.series) }))
    .filter((c): c is Confounder & { at: string } => c.at !== null);

  const tickFormatter = (value: number) => {
    if (metric.unit === 'percent') return `${value}%`;
    if (metric.unit === 'currency') return `$${value}`;
    return `${value}`;
  };

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="py-6 gap-0 border-b">
        <CardTitle className="text-lg font-semibold">{metric.label}</CardTitle>
        <CardDescription>{metric.description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 pb-6 [--marker:#d97706] dark:[--marker:#fbbf24] [--confounder-1:#7c3aed] dark:[--confounder-1:#a78bfa] [--confounder-2:#0d9488] dark:[--confounder-2:#2dd4bf] [--confounder-3:#db2777] dark:[--confounder-3:#f472b6] [--m-email:#2563eb] dark:[--m-email:#3b82f6] [--m-google:#db2777] dark:[--m-google:#ec4899] [--m-slack:#0891b2] [--m-other:#b45309]">
        {/* Marker colours are not design tokens, so they are declared here
            (where both the chart and the legend can read them) and flipped for
            dark mode rather than hardcoded into the SVG. */}
        <ChartContainer config={config} className="h-[220px] w-full">
          {isBars ? (
            <BarChart
              data={barData}
              accessibilityLayer
              margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="0" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                minTickGap={50}
                tickFormatter={(v) => axisWindow(String(v), metric.trailingDays)}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                width={48}
                tickFormatter={tickFormatter}
              />
              {shownConfounders.map((c, i) => (
                <ReferenceLine
                  key={c.slug}
                  x={c.at}
                  stroke={`var(${confounderStyle(i).varName})`}
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                  strokeDasharray={confounderStyle(i).dash}
                />
              ))}
              {changeAt !== null && (
                <ReferenceLine
                  x={changeAt}
                  stroke="var(--marker)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
              )}
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) => weekLabel(String(label), metric.trailingDays)}
                  />
                }
              />
              {/* The card stroke gives each stacked segment a hairline gap so
                  adjacent colours never touch. */}
              {barKeys.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId={isStacked ? 'method' : undefined}
                  fill={`var(--color-${k})`}
                  stroke="var(--card)"
                  strokeWidth={1}
                  maxBarSize={18}
                  radius={i === barKeys.length - 1 ? [3, 3, 0, 0] : 0}
                />
              ))}
            </BarChart>
          ) : (
          <LineChart
            data={metric.series}
            accessibilityLayer
            margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="0" />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              minTickGap={50}
              tickFormatter={(v) => axisWindow(String(v), metric.trailingDays)}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              width={48}
              tickFormatter={tickFormatter}
            />

            {/* Other changes first, so the emphasised one paints over them. */}
            {shownConfounders.map((c, i) => (
              <ReferenceLine
                key={c.slug}
                x={c.at}
                stroke={`var(${confounderStyle(i).varName})`}
                strokeOpacity={0.55}
                strokeWidth={1.5}
                strokeDasharray={confounderStyle(i).dash}
              />
            ))}
            {changeAt !== null && (
              <ReferenceLine
                x={changeAt}
                stroke="var(--marker)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
            )}

            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(label) => weekLabel(String(label), metric.trailingDays)}
                  formatter={(value, _name, item: any) => {
                    const point = item?.payload as MetricPoint | undefined;
                    return (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: 'var(--chart-3)' }}
                        />
                        <div className="flex flex-1 flex-col gap-1">
                          <div className="flex justify-between leading-none items-center gap-4">
                            <span className="text-muted-foreground">{metric.label}</span>
                            <span className="text-foreground font-mono font-medium tabular-nums">
                              {formatMetricValue(Number(value), metric.unit)}
                            </span>
                          </div>
                          {/* The counts behind a rate: without them a point
                              built on one person looks identical to one built
                              on fifty. The window itself is the tooltip's
                              heading, so it is not repeated here. */}
                          {point && metric.unit !== 'count' && (
                            <div className="text-[11px] text-muted-foreground tabular-nums">
                              {metric.unit === 'currency'
                                ? `$${point.numerator.toLocaleString()} new MRR from ${countLabel(point.denominator, population)}`
                                : `${point.numerator} of ${countLabel(point.denominator, population)}`}
                            </div>
                          )}
                          {/* The split behind the number; hidden entirely when
                              nothing beyond the numerator remains to say. */}
                          {point?.breakdown && breakdownLabel(point.breakdown) !== '' && (
                            <div className="text-[11px] text-muted-foreground tabular-nums">
                              {breakdownLabel(point.breakdown)}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  }}
                />
              }
            />

            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--chart-3)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
            />
          </LineChart>
          )}
        </ChartContainer>

        {/* Incomplete points are dropped by the API, so a chart that stops
            short of today is correct. Say so, or it reads as missing data. */}
        {metric.completeThrough !== null && metric.windowDays !== null && (
          <p className="mt-3 text-xs text-muted-foreground">
            Complete through {shortDate(metric.completeThrough)}. {population.newest} after that have
            not had their {metric.windowDays} {metric.windowDays === 1 ? 'day' : 'days'} yet.
          </p>
        )}

        {(changeAt !== null || shownConfounders.length > 0 || isStacked) && (
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 border-t pt-3 text-xs text-muted-foreground">
            {/* Series first: squares for the stacked methods, then the marker
                dots. Solid shapes, not 1.5px line samples: a legend key has to
                be visible at a glance. */}
            {isStacked &&
              stackKeys.map((k) => (
                <span key={k} className="inline-flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: METHOD_VARS[k] }}
                  />
                  {BREAKDOWN_LABELS[k] ?? k}
                </span>
              ))}
            {changeAt !== null && (
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: 'var(--marker)' }}
                />
                This change, {shortDate(changeDate)}
              </span>
            )}
            {shownConfounders.map((c, i) => (
              <span key={c.slug} className="inline-flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: `var(${confounderStyle(i).varName})` }}
                />
                {c.title}, {shortDate(c.date)}
              </span>
            ))}
          </div>
        )}

        {metric.cohorts && (
          <CohortBreakdown
            cohorts={metric.cohorts}
            changeDate={changeDate}
            slug={slug}
            metricKey={metric.key}
            unit={metric.unit}
            population={population}
            caption={
              metric.cohorts.caption ??
              (metric.unit === 'currency'
                ? 'New MRR per signup, by how long after signing up they started a paid plan (trials count once they pay)'
                : 'By how long after signing up they started a paid plan (on a trial or bought outright)')
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ImpactChart;
